import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

interface User {
  id: string;
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  studio_name?: string;
  subscription_plan?: string;
  role?: 'admin' | 'studio_owner' | 'producer' | 'staff';
  commission_rate?: number;
  allowed_sections?: string[];
  studio_owner_id?: number;
  date_joined?: string;
  last_login?: string;
  staff_title?: string;
  salary?: number;
  salary_currency?: string;
}

interface AuthContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  staffLogin: (accessKey: string) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isStudioOwner: boolean;
  isStaff: boolean;
  canAccess: (section: string) => boolean;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('access_token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const storedToken = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        try {
          const profileResponse = await authAPI.getProfile();
          const refreshedUser = profileResponse.data || JSON.parse(storedUser);
          localStorage.setItem('user', JSON.stringify(refreshedUser));
          setUser(refreshedUser);
          setToken(storedToken);
        } catch (err) {
          console.error('Session validation failed:', err);
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          setUser(null);
          setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    try {
      setLoading(true);
      const response = await authAPI.login({ username, password });
      const { access, user } = response.data;
      
      localStorage.setItem('access_token', access);
      localStorage.setItem('user', JSON.stringify(user));
      
      setUser(user);
      setToken(access);
      setError(null);
      return user;
    } catch (err) {
      console.error('Login error:', err);
      setError('Invalid credentials. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const staffLogin = useCallback(async (accessKey: string) => {
    try {
      setLoading(true);
      const response = await authAPI.staffLogin(accessKey);
      const { access, user } = response.data;
      
      localStorage.setItem('access_token', access);
      localStorage.setItem('user', JSON.stringify(user));
      
      setUser(user);
      setToken(access);
      setError(null);
      return user;
    } catch (err: any) {
      console.error('Producer login error:', err);
      setError(err?.response?.data?.error || 'Invalid access key. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (userData: any) => {
    try {
      setLoading(true);
      const response = await authAPI.register(userData);
      return response.data;
    } catch (err) {
      console.error('Registration error:', err);
      setError('Registration failed. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    setUser(null);
    setToken(null);
    setError(null);
  }, []);

  const isStudioOwner = user?.role === 'studio_owner';
  const isStaff = user?.role === 'producer' || user?.role === 'staff';
  
  const canAccess = useCallback((section: string) => {
    if (!user) return false;
    if (user.role === 'studio_owner') return true;
    if (user.role === 'producer' || user.role === 'staff') {
      return user.allowed_sections?.includes(section) ?? false;
    }
    return false;
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        token,
        login,
        staffLogin,
        register,
        logout,
        isAuthenticated: !!user,
        isStudioOwner,
        isStaff,
        canAccess,
        loading,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
